import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import '../styles/Main.css';
import logo from '../assets/logo1.png';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { FaLayerGroup, FaMap, FaMountain, FaBuilding } from 'react-icons/fa';
import Calendar from 'react-calendar';   // ✅ NEW
import { parseISO } from 'date-fns';

mapboxgl.accessToken =
  'pk.eyJ1IjoicWpiZmVycmVyIiwiYSI6ImNtYTlqbDEyaTBrYnUya3BzeHd4ZWFnOXMifQ.PeNfgVuGD53Au8Vmkpe2RQ';

// ✅ cache for geocoding
const addressCache = {};
const getAddressFromCoords = async (lat, lng) => {
  const key = `${lat},${lng}`;
  if (addressCache[key]) return addressCache[key];

  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxgl.accessToken}`;
  try {
    const response = await fetch(url);
    const data = await response.json();
    if (data.features.length > 0) {
      addressCache[key] = data.features[0].place_name;
      return data.features[0].place_name;
    }
    return 'Unknown';
  } catch (error) {
    console.error('Error fetching address:', error);
    return 'Unknown';
  }
};

const pestColors = {
  'Fall Armyworm': 'violet',
  'Onion Armyworm': 'green',
  'Cutworm': 'red',
};

const Main = () => {
  const mapContainerRef = useRef(null);
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [selectedPests, setSelectedPests] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [viewMode, setViewMode] = useState('3D');
  const [mapMode, setMapMode] = useState('satellite');
  const [mapStyle, setMapStyle] = useState(
    'mapbox://styles/mapbox/satellite-streets-v12'
  );
  const [terrainEnabled, setTerrainEnabled] = useState(true);
  const [buildingsEnabled, setBuildingsEnabled] = useState(false);


  // 🔽 new states
  const [dates, setDates] = useState([]); // available dates
  const [selectedDate, setSelectedDate] = useState(null);

  const clearMarkers = () => {
    markersRef.current.forEach((marker) => marker.remove());
    markersRef.current = [];
  };

  // 🔽 fetch available dates once
  useEffect(() => {
    const fetchDates = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'pestScans'));
        const uniqueDates = new Set();
        snapshot.forEach((doc) => {
          const data = doc.data();
          if (data.date) {
            const dateObj = parseISO(data.date);
            if (!isNaN(dateObj)) {
              uniqueDates.add(dateObj.toDateString());
            }
          }
        });
        setDates(
          Array.from(uniqueDates)
            .map((d) => new Date(d))
            .sort((a, b) => a - b)
        );
      } catch (err) {
        console.error('Error fetching dates:', err);
      }
    };
    fetchDates();
  }, []);

  
const fetchPestReports = async (map, selectedPests, selectedDate, selectedStatus) => {
  try {
    const snapshot = await getDocs(collection(db, 'pestScans'));

    for (const doc of snapshot.docs) {
      const data = doc.data();
      const pest = data.result;

      // Skip empty, null, or unrecognized pests
      if (!pest || !pestColors[pest]) continue;

      const status = data.verificationStatus || 'Pending';

      // filter by pests
      if (selectedPests.length > 0 && !selectedPests.includes(pest)) continue;

      // filter by date
      if (selectedDate) {
        const reportDate = parseISO(data.date);
        if (!reportDate || reportDate.toDateString() !== selectedDate.toDateString()) continue;
      }

      // filter by status
      if (selectedStatus.length > 0 && !selectedStatus.includes(status)) continue;

      if (data.latitude && data.longitude) {
        const address = await getAddressFromCoords(data.latitude, data.longitude);
        const imgId = `pest-img-${doc.id}`;

        const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
          <div class="pest-popup">
            <h3 class="pest-popup-title">${pest}</h3>
            <p><strong>Status:</strong> ${status}</p>
            <p><strong>Reported by:</strong> ${data.farmerName || 'Anonymous'}</p>
            <p><strong>Date Reported:</strong> ${data.date ? `${data.date}, ${data.time}` : 'Unknown'}</p>
            <p><strong>Location:</strong> ${address}</p>
            <p><strong>Latitude & Longitude:</strong> ${data.latitude}, ${data.longitude}</p>
            ${
              data.image
                ? `<img src="${data.image}" alt="Pest" id="${imgId}" class="pest-popup-image" />`
                : ''
            }
          </div>
        `);

        const markerColor = pestColors[pest];

        const marker = new mapboxgl.Marker({ color: markerColor })
          .setLngLat([data.longitude, data.latitude])
          .setPopup(popup)
          .addTo(map);

        // Right-click to "fly" to the marker
        marker.getElement().addEventListener('contextmenu', (event) => {
          event.preventDefault();

          map.flyTo({
            center: [data.longitude, data.latitude],
            zoom: 18,
            pitch: 60,
            speed: 1.2,
            curve: 1.6,
            essential: true,
          });

          const handleMoveEnd = () => {
            popup.addTo(map);
            map.off('moveend', handleMoveEnd);
          };

          map.on('moveend', handleMoveEnd);
        });

        // Popup image click navigation
        marker.getPopup().on('open', () => {
          const imgElement = document.getElementById(imgId);
          if (imgElement) {
            imgElement.onclick = () =>
              navigate('/results', { state: { reportId: doc.id } });
          }
        });

        markersRef.current.push(marker);
      }
    }
  } catch (error) {
    console.error('Failed to load pest reports:', error);
  }
};

  // eslint-disable-next-line
  useEffect(() => {
    if (mapRef.current && mapRef.current.isStyleLoaded()) {
      clearMarkers();
      fetchPestReports(mapRef.current, selectedPests, selectedDate, selectedStatus);
    }
  }, [selectedPests, selectedDate, selectedStatus]);

const initializeMapFeatures = (mapInstance) => {
  // 🌍 Terrain setup
  mapInstance.addSource('mapbox-dem', {
    type: 'raster-dem',
    url: 'mapbox://mapbox.terrain-rgb',
    tileSize: 512,
    maxzoom: 14,
  });

  mapInstance.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });

  // 🌤️ Add sky
  mapInstance.addLayer({
    id: 'sky',
    type: 'sky',
    paint: {
      'sky-type': 'atmosphere',
      'sky-atmosphere-sun': [0.0, 0.0],
      'sky-atmosphere-sun-intensity': 15,
    },
  });

const styleName = mapInstance.getStyle().name?.toLowerCase() || '';

if (styleName.includes('satellite')) {
  // 🛰️ Satellite style: use classic 3D building layer
  mapInstance.addLayer(
    {
      id: '3d-buildings',
      source: 'composite',
      'source-layer': 'building',
      filter: ['==', 'extrude', 'true'],
      type: 'fill-extrusion',
      minzoom: 15,
      layout: { visibility: 'none' }, // 👈 add this line!
      paint: {
        'fill-extrusion-color': [
          'interpolate',
          ['linear'],
          ['get', 'height'],
          0, '#e5e2dc',
          30, '#d4cec4',
          100, '#bdb7ac',
        ],
        'fill-extrusion-vertical-gradient': true,
        'fill-extrusion-height': [
          'interpolate',
          ['linear'],
          ['zoom'],
          15,
          0,
          15.05,
          ['get', 'height'],
        ],
        'fill-extrusion-base': [
          'interpolate',
          ['linear'],
          ['zoom'],
          15,
          0,
          15.05,
          ['get', 'min_height'],
        ],
        'fill-extrusion-opacity': 1.0,
      },
    },
    'waterway-label'
  );
} else if (styleName.includes('standard') || styleName.includes('streets')) {
  // 🧱 Default or Standard style — make buildings toggleable
  mapInstance.addLayer(
    {
      id: '3d-buildings',
      type: 'fill-extrusion',
      source: 'composite',
      'source-layer': 'building',
      minzoom: 15,
      paint: {
        'fill-extrusion-color': '#d6d3cd',
        'fill-extrusion-height': ['get', 'height'],
        'fill-extrusion-base': ['get', 'min_height'],
        'fill-extrusion-opacity': 0.9,
      },
    },
    'waterway-label'
  );
}
  // ☀️ Add soft sunlight
  mapInstance.setLight({
    anchor: 'viewport',
    color: 'white',
    intensity: 0.6,
    position: [1.15, 210, 60],
  });

  // Load your reports
  fetchPestReports(mapInstance, selectedPests, selectedDate, selectedStatus);
};

  // eslint-disable-next-line
  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapStyle,
      center: [121.04599, 14.57],
      zoom: 10,
      pitch: 70,
      bearing: 10,
      antialias: true,
    });

    mapRef.current = map;
    map.addControl(new mapboxgl.NavigationControl());

    map.on('load', () => {
      initializeMapFeatures(map);
    });

    return () => {
      clearMarkers();
      map.remove();
    };
  }, [mapStyle]);

  const handleCheckboxChange = (event) => {
    const value = event.target.value;
    const isChecked = event.target.checked;
    setSelectedPests((prev) =>
      isChecked ? [...prev, value] : prev.filter((pest) => pest !== value)
    );
  };

  const handleStatusChange = (event) => {
    const value = event.target.value;
    const isChecked = event.target.checked;
    setSelectedStatus((prev) =>
      isChecked ? [...prev, value] : prev.filter((status) => status !== value)
    );
  };

  const handleLogoClick = () => navigate('/home');

  const set2DView = () => {
    if (mapRef.current) {
      mapRef.current.easeTo({ pitch: 0, bearing: 0, duration: 1000 });
      setViewMode('2D');
    }
  };

  const set3DView = () => {
    if (mapRef.current) {
      mapRef.current.easeTo({ pitch: 70, bearing: 10, duration: 1000 });
      setViewMode('3D');
    }
  };

  const toggleTerrain = () => {
    const map = mapRef.current;
    if (!map) return;
    if (terrainEnabled) {
      map.setTerrain(null);
      setTerrainEnabled(false);
    } else {
      map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });
      setTerrainEnabled(true);
    }
  };

const toggleBuildings = () => {
  const map = mapRef.current;
  if (!map) return;

  const layerId = '3d-buildings';

  // Wait until style is loaded (important after switching map styles)
  if (!map.isStyleLoaded()) return;

  if (!map.getLayer(layerId)) {
    console.warn('3D building layer not found in this style.');
    return;
  }

  const visibility = map.getLayoutProperty(layerId, 'visibility');
  const newVisibility = visibility === 'visible' ? 'none' : 'visible';
  map.setLayoutProperty(layerId, 'visibility', newVisibility);
  setBuildingsEnabled(newVisibility === 'visible');
};


  // ✅ check if a tile is disabled (not in Firestore dates)
  const tileDisabled = ({ date }) => {
    return !dates.some((d) => d.toDateString() === date.toDateString());
  };

  return (
    <div className="main-container">
      <aside className="sidebar">
        <img
          src={logo}
          alt="Onion Pest Logo"
          className="logo"
          onClick={handleLogoClick}
          style={{ cursor: 'pointer' }}
        />

        <div className="card">
          <h3>Available Dates</h3>
        <Calendar
          onChange={setSelectedDate}
          value={selectedDate}
          tileDisabled={tileDisabled}
          tileClassName={({ date, view }) => {
            if (view === 'month') {
              const isAvailable = dates.some((d) => d.toDateString() === date.toDateString());
              if (isAvailable) {
                return 'available-day';
              }
            }
            return null;
          }}
        />
          <button onClick={() => setSelectedDate(null)}>Clear</button>

          <h5>PEST CATEGORIES</h5>
          <div className="checkbox-group">
            {['Cutworm', 'Fall Armyworm', 'Onion Armyworm'].map((pest) => (
              <label key={pest}>
                <input
                  type="checkbox"
                  value={pest}
                  checked={selectedPests.includes(pest)}
                  onChange={handleCheckboxChange}
                />
                {pest}
              </label>
            ))}
          </div>

          <h5>VERIFICATION STATUS</h5>
          <div className="checkbox-group">
            {['Pending', 'Verified'].map((status) => (
              <label key={status}>
                <input
                  type="checkbox"
                  value={status}
                  checked={selectedStatus.includes(status)}
                  onChange={handleStatusChange}
                />
                {status}
              </label>
            ))}
          </div>
        </div>

        <div className="card">
          <h3>MAP SETTINGS</h3>
          <div className="map-style-toggle">
            <div className="toggle-group">
              <div className="toggle-group-title">
                <FaLayerGroup style={{ marginRight: 6 }} /> Map View
              </div>
              <div className="toggle-group-buttons">
                <button
                  className={`toggle-button ${viewMode === '2D' ? 'active' : ''}`}
                  onClick={set2DView}
                >
                  2D
                </button>
                <button
                  className={`toggle-button ${viewMode === '3D' ? 'active' : ''}`}
                  onClick={set3DView}
                >
                  3D
                </button>
              </div>
            </div>

            <div className="toggle-group">
              <div className="toggle-group-title">
                <FaMap style={{ marginRight: 6 }} /> Map Type
              </div>
              <div className="toggle-group-buttons">
                <button
                  className={`toggle-button ${mapMode === 'default' ? 'active' : ''}`}
                  onClick={() => {
                    setMapStyle('mapbox://styles/mapbox/standard');
                    setMapMode('default');
                  }}
                >
                  Default
                </button>
                <button
                  className={`toggle-button ${mapMode === 'satellite' ? 'active' : ''}`}
                  onClick={() => {
                    setMapStyle('mapbox://styles/mapbox/satellite-streets-v12');
                    setMapMode('satellite');
                  }}
                >
                  Satellite
                </button>
              </div>
            </div>

            <div className="toggle-group">
              <div className="toggle-group-title">
                <FaMountain style={{ marginRight: 6 }} /> Terrain
              </div>
              <div className="toggle-group-buttons">
                <button
                  className={`toggle-button ${terrainEnabled ? 'active' : ''}`}
                  onClick={toggleTerrain}
                >
                  {terrainEnabled ? 'On' : 'Off'}
                </button>
              </div>
            </div>

            {/* ✅ Add this inside the same section */}
            <div className="toggle-group">
              <div className="toggle-group-title">
                <FaBuilding style={{ marginRight: 6 }} /> Buildings
              </div>
              <div className="toggle-group-buttons">
                <button
                  className={`toggle-button ${buildingsEnabled ? 'active' : ''}`}
                  onClick={toggleBuildings}
                >
                  {buildingsEnabled ? 'On' : 'Off'}
                </button>
              </div>
            </div>

          </div>
        </div>
      </aside>

      <main className="map-area">
        <div ref={mapContainerRef} id="map" />
      </main>
    </div>
  );
};

export default Main;
